import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useState,
    FC,
    ReactNode,
} from "react";

/** Ordre de priorité : la première modale demandée dans cette liste est affichée. CGU en priorité absolue. */
const MODAL_ORDER = ["cgu", "profileIncomplete", "fundingReminder"] as const;

export type PostLoginModalId = (typeof MODAL_ORDER)[number];

type PostLoginModalContextType = {
    /** Id de la modale actuellement autorisée à s'afficher (une seule à la fois). */
    currentModal: PostLoginModalId | null;
    /** Demande l'affichage de cette modale (ajout à la file, priorité selon MODAL_ORDER). */
    requestModal: (id: PostLoginModalId) => void;
    /** Ferme la modale courante et passe à la suivante dans la file. */
    dismissModal: () => void;
};

const PostLoginModalContext = createContext<
    PostLoginModalContextType | undefined
>(undefined);

export const PostLoginModalProvider: FC<{ children: ReactNode }> = ({
    children,
}) => {
    const [requested, setRequested] = useState<Set<PostLoginModalId>>(
        new Set()
    );

    const currentModal = useMemo(() => {
        for (const id of MODAL_ORDER) {
            if (requested.has(id)) return id;
        }
        return null;
    }, [requested]);

    const requestModal = useCallback((id: PostLoginModalId) => {
        setRequested((prev) => new Set(prev).add(id));
    }, []);

    const dismissModal = useCallback(() => {
        if (currentModal === null) return;
        setRequested((prev) => {
            const next = new Set(prev);
            next.delete(currentModal);
            return next;
        });
    }, [currentModal]);

    const value = useMemo(
        () => ({ currentModal, requestModal, dismissModal }),
        [currentModal, requestModal, dismissModal]
    );

    return (
        <PostLoginModalContext.Provider value={value}>
            {children}
        </PostLoginModalContext.Provider>
    );
};

export function usePostLoginModal(): PostLoginModalContextType {
    const ctx = useContext(PostLoginModalContext);
    if (ctx === undefined) {
        throw new Error(
            "usePostLoginModal must be used within PostLoginModalProvider"
        );
    }
    return ctx;
}
